import {
  CASHFLOW_PERIOD_MONTH,
  CASHFLOW_YEAR_FINANCIAL,
  FINANCIAL_YEAR_START_MONTH,
} from 'constants/uiConstants';
import type {
  CashflowPeriod,
  CashflowYearMode,
} from '../../../../../redux/slices/InsightsSlice';
import {
  COST_CATEGORY_ORDER,
  STEP_TYPE_TO_COST_CATEGORY,
} from 'pages/FarmsPage/Workspace/Insights/costCategories';

/** Cost categories shown as Expenditure groups, in display order — the shared set from costCategories.ts. */
export const CASHFLOW_COST_CATEGORIES = COST_CATEGORY_ORDER;

/** Step type → cost category — the shared mapping from costCategories.ts (Seed dress counts as Seed). */
export const STEP_TYPE_TO_CASHFLOW_CATEGORY = STEP_TYPE_TO_COST_CATEGORY;

const MONTH_ABBR = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/** One calendar month within the resolved cashflow year window. */
export interface CashflowPeriodMonth {
  calendarYear: number;
  /** 0-11, calendar month index (UTC). */
  monthIndex: number;
}

/** One column in the Cashflow grid — a single month, or a rolled-up quarter of 3 months. */
export interface CashflowPeriodColumn {
  label: string;
  months: CashflowPeriodMonth[];
}

/**
 * The 12 calendar months making up the selected year window, in display
 * order — Jan(year)-Dec(year) for a calendar year, or
 * FINANCIAL_YEAR_START_MONTH(year)-through-the-same-month(year+1) for a
 * financial year (e.g. FY2025 = Jul 2025 - Jun 2026).
 */
const resolveWindowMonths = (
  year: number,
  yearMode: CashflowYearMode
): (CashflowPeriodMonth & { label: string })[] => {
  const startMonth =
    yearMode === CASHFLOW_YEAR_FINANCIAL ? FINANCIAL_YEAR_START_MONTH : 0;
  return Array.from({ length: 12 }, (_, i) => {
    const monthIndex = (startMonth + i) % 12;
    const calendarYear = monthIndex < startMonth ? year + 1 : year;
    return { calendarYear, monthIndex, label: MONTH_ABBR[monthIndex] };
  });
};

/** Builds the ordered period columns (12 months, or 4 quarters of 3 months each) for the selected year/period settings. */
export const buildCashflowPeriods = (
  year: number,
  yearMode: CashflowYearMode,
  period: CashflowPeriod
): CashflowPeriodColumn[] => {
  const months = resolveWindowMonths(year, yearMode);

  if (period === CASHFLOW_PERIOD_MONTH) {
    return months.map((m) => ({ label: m.label, months: [m] }));
  }

  return Array.from({ length: 4 }, (_, q) => {
    const quarterMonths = months.slice(q * 3, q * 3 + 3);
    return {
      label: `Q${q + 1} (${quarterMonths[0].label}–${quarterMonths[2].label})`,
      months: quarterMonths,
    };
  });
};

/**
 * Which period column a resolved date falls into. `'noDate'` when there's no
 * date at all to bucket by; `'outOfWindow'` when the date falls in a
 * calendar month outside the selected year — a single year's cashflow
 * shouldn't silently absorb unrelated years' data.
 */
export const resolveCashflowPeriodIndex = (
  dateStr: string | null,
  periods: CashflowPeriodColumn[]
): number | 'noDate' | 'outOfWindow' => {
  if (!dateStr) return 'noDate';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return 'noDate';
  const calendarYear = date.getUTCFullYear();
  const monthIndex = date.getUTCMonth();
  const index = periods.findIndex((p) =>
    p.months.some(
      (m) => m.calendarYear === calendarYear && m.monthIndex === monthIndex
    )
  );
  return index === -1 ? 'outOfWindow' : index;
};

/** e.g. "2026" for a calendar year, "FY 2025–26" for a financial year. */
export const formatCashflowYearLabel = (
  year: number,
  yearMode: CashflowYearMode
): string =>
  yearMode === CASHFLOW_YEAR_FINANCIAL
    ? `FY ${year}–${String(year + 1).slice(-2)}`
    : String(year);
